import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api, Product, Category } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { WEB_BASE_URL } from '../config/environment';
import './Products.css';
import { handleImageError } from '../utils/imageUtils';

// Helper function to get proper image URL
const getImageUrl = (imagePath: string | undefined): string => {
  if (!imagePath) {
    return `${WEB_BASE_URL}/products/IMG_2794.jpeg`;
  }
  
  // Tigris private URLs → proxy (legacy fallback)
  if (imagePath.includes('storageapi.dev') || imagePath.includes('railway.storage') || imagePath.includes('.r2.dev') || imagePath.includes('r2.cloudflarestorage.com')) {
    const key = imagePath.replace(/^https?:\/\/[^/]+\//, '');
    return `${WEB_BASE_URL}/api/upload/proxy/${key}`;
  }

  // Public Cloudflare R2, proxy URLs, or other absolute URLs → use directly
  if (imagePath.startsWith('https://') || imagePath.startsWith('http://') || imagePath.startsWith('/api/upload/proxy/')) {
    return imagePath;
  }

  // Base64 → fallback
  if (imagePath.startsWith('data:image/')) {
    return `${WEB_BASE_URL}/products/IMG_2794.jpeg`;
  }

  // Static file path served by backend
  if (imagePath.startsWith('/products/')) {
    return `${WEB_BASE_URL}${imagePath}`;
  }

  return `${WEB_BASE_URL}/products/IMG_2794.jpeg`;
};

const Products: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [priceRange, setPriceRange] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [availableSizes, setAvailableSizes] = useState<string[]>([]);
  const [showSizeModal, setShowSizeModal] = useState<boolean>(false);
  const { addToCart: addToCartContext, state: cartState } = useCart();
  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    loadCategories();
    // Initial load of products
    loadProducts();
  }, []);

  // Handle URL parameters
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam !== selectedCategory) {
      setSelectedCategory(categoryParam || '');
    }
  }, [searchParams]);

  const loadCategories = async () => {
    try {
      const categoriesData = await api.getCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadSizes = async (categoryId?: number) => {
    try {
      const sizesData = await api.getSizesByCategory(categoryId);
      setAvailableSizes(sizesData);
    } catch (error) {
      console.error('Failed to load sizes:', error);
      setAvailableSizes([]);
    }
  };

  const loadProducts = async () => {
    try {
      console.log('🔍 Products: Starting loadProducts...');
      setLoading(true);
      setError('');
      
      // TRY API FIRST
      try {
        console.log('🌐 Products: Calling api.getProducts...');
        const response = await api.getProducts({
          search: searchQuery || undefined,
          category: selectedCategory || undefined
        });
        console.log('✅ Products: Got response:', response?.products?.length, 'products');
        setProducts(response.products);
        return;
      } catch (apiError) {
        console.log('❌ API failed, trying direct backend call...');
      }
      
      // FALLBACK: DIRECT BACKEND CALL
      try {
        const directResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/products`);
        if (directResponse.ok) {
          const data = await directResponse.json();
          console.log('✅ Direct backend call success:', data?.products?.length, 'products');
          setProducts(data.products || []);
          return;
        }
      } catch (directError) {
        console.log('❌ Direct backend call failed too');
      }
      
      throw new Error('Both API calls failed');
    } catch (err: any) {
      setError(err.message || 'Failed to load products');
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  };

  // Reload products when search or category changes
  useEffect(() => {
    loadProducts();
  }, [searchQuery, selectedCategory]);

  // Load sizes when category changes
  useEffect(() => {
    if (selectedCategory) {
      const category = categories.find(cat => cat.name.toLowerCase() === selectedCategory.toLowerCase());
      if (category) {
        loadSizes(category.id);
      }
      // Clear selected size when category changes
      setSelectedSize('');
    } else {
      setAvailableSizes([]);
      setSelectedSize('');
    }
  }, [selectedCategory, categories]);

  // Filter and search logic (local filtering on client side)
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      // Category filtering (if using local filtering)
      const categoryMatch = !selectedCategory || 
        (product.categories?.length
          ? product.categories.some(c => c.name?.toLowerCase() === selectedCategory.toLowerCase())
          : product.category?.name?.toLowerCase() === selectedCategory.toLowerCase());
      
      // Size filtering
      const sizeMatch = !selectedSize || product.size === selectedSize;
      
      // Price filtering
      let priceMatch = true;
      if (priceRange) {
        const [min, max] = priceRange.split('-').map(Number);
        priceMatch = product.price >= min && (max ? product.price <= max : true);
      }
      
      return categoryMatch && sizeMatch && priceMatch;
    });
  }, [selectedCategory, selectedSize, priceRange, products]);

  // Sort products
  const sortedProducts = useMemo(() => {
    const sorted = [...filteredProducts];
    switch (sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price-high':
        return sorted.sort((a, b) => b.price - a.price);
      case 'name':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'newest':
        return sorted.sort((a, b) => new Date(b.id).getTime() - new Date(a.id).getTime()); // Use ID as proxy for newest
      default:
        return sorted;
    }
  }, [filteredProducts, sortBy]);

  const handleAddToCart = async (productId: number, productSize?: string) => {
    try {
      // Find the product from current products list to pass full product data
      const product = products.find(p => p.id === productId);
      console.log('Adding product to cart:', product); // Debug log
      await addToCartContext(productId, 1, productSize);
    } catch (error) {
      console.error('Failed to add to cart:', error);
    }
  };


  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedSize('');
    setPriceRange('');
    setSearchQuery('');
  };

  const handleSizeDropdownClick = () => {
    if (!selectedCategory) {
      setShowSizeModal(true);
      return false;
    }
    return true;
  };

  // Check if category has sizes (categories like glasses/accessories might not have sizes)
  const categoryHasSizes = (categoryName: string) => {
    const noSizeCategories = ['glasses', 'accessories', 'jewelry', 'bags'];
    return !noSizeCategories.includes(categoryName.toLowerCase());
  };

  return (
    <div className="products-page">
      {/* Hero Section */}
      <div className="products-hero">
        <div className="container">
          <div className="hero-content">
            <h1>
              <span className="gradient-text">Vintage Collection</span>
            </h1>
            <p className="hero-subtitle">Discover unique pieces with timeless style</p>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">{products.length}+</span>
                <span className="stat-label">Unique Pieces</span>
              </div>
              <div className="stat">
                <span className="stat-number">
                  {products.filter(p => p.inStock).length}
                </span>
                <span className="stat-label">In Stock</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="main-layout">
          {/* Filter Section - Left Sidebar */}
          <div className="filters-section">
            <div className="filters-header">
              <div className="filters-title">
                <h2>Find Your Style</h2>
              </div>
            </div>

            <div className="filters-content">
              <div className="filters-grid">
                <div className="filter-group">
                  <label>Category</label>
                  <select 
                    value={selectedCategory} 
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">All Categories</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.name.toLowerCase()}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label>Size</label>
                  <div className="filter-select-wrapper">
                    <select 
                      value={selectedSize} 
                      onChange={(e) => setSelectedSize(e.target.value)}
                      onClick={handleSizeDropdownClick}
                      className="filter-select"
                      disabled={!selectedCategory || !categoryHasSizes(selectedCategory)}
                    >
                      <option value="">All Sizes</option>
                      {availableSizes.map(size => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                    {(!selectedCategory || !categoryHasSizes(selectedCategory)) && (
                      <div className="filter-tooltip">
                        Select a category first! :)
                      </div>
                    )}
                  </div>
                </div>

                <div className="filter-group">
                  <label>Price Range</label>
                  <select 
                    value={priceRange} 
                    onChange={(e) => setPriceRange(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">All Prices</option>
                    <option value="0-40">Under €40</option>
                    <option value="40-70">€40 - €70</option>
                    <option value="70-100">€70 - €100</option>
                    <option value="100-999">€100+</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Sort By</label>
                  <select 
                    value={sortBy} 
                    onChange={(e) => setSortBy(e.target.value)}
                    className="filter-select"
                  >
                    <option value="newest">Newest</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="name">Name A-Z</option>
                    <option value="rating">Highest Rated</option>
                  </select>
                </div>
              </div>

              <div className="search-section">
                <div className="search-wrapper">
                  <input 
                    type="text" 
                    placeholder="Search products..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  <div className="search-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
                
                {(selectedCategory || selectedSize || priceRange || searchQuery) && (
                  <button className="clear-filters" onClick={clearFilters}>
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Products Content - Right Side */}
          <div className="products-content">
            {/* Loading State */}
            {loading && (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading products...</p>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="error-state">
                <div className="error-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9V13M12 17.0195V17M10.29 3.86L1.82 18C1.64468 18.3024 1.55274 18.6453 1.55376 18.9945C1.55478 19.3437 1.64868 19.6861 1.82601 19.9877C2.00333 20.2893 2.25952 20.5368 2.56983 20.7039C2.88014 20.871 3.22929 20.9519 3.58 20.94H20.42C20.7707 20.9519 21.1199 20.871 21.4302 20.7039C21.7405 20.5368 21.9967 20.2893 22.174 19.9877C22.3513 19.6861 22.4452 19.3437 22.4462 18.9945C22.4473 18.6453 22.3553 18.3024 22.18 18L13.71 3.86C13.5318 3.56611 13.2807 3.32312 12.9812 3.15448C12.6817 2.98585 12.3438 2.89725 12 2.89725C11.6562 2.89725 11.3183 2.98585 11.0188 3.15448C10.7193 3.32312 10.4682 3.56611 10.29 3.86V3.86Z" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>Error Loading Products</h3>
                <p>{error}</p>
                <button onClick={loadProducts} className="retry-btn">
                  Try Again
                </button>
              </div>
            )}

            {/* Results Header */}
            {!loading && !error && (
              <div className="results-header">
                <h3>
                  {sortedProducts.length} {sortedProducts.length === 1 ? 'Product' : 'Products'} Found
                </h3>
              </div>
            )}

            {/* Active Filters */}
            {(selectedCategory || selectedSize || priceRange || searchQuery) && (
              <div className="active-filters-row">
                <div className="active-filters">
                  {selectedCategory && (
                    <span className="filter-tag">
                      {selectedCategory}
                      <button onClick={() => setSelectedCategory('')}>×</button>
                    </span>
                  )}
                  {selectedSize && (
                    <span className="filter-tag">
                      Size {selectedSize}
                      <button onClick={() => setSelectedSize('')}>×</button>
                    </span>
                  )}
                  {priceRange && (
                    <span className="filter-tag">
                      €{priceRange.replace('-', ' - €')}
                      <button onClick={() => setPriceRange('')}>×</button>
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Products Grid */}
            {!loading && !error && (
              <div className="products-grid-custom">
                {sortedProducts.length > 0 ? (
                  sortedProducts.map(product => (
                    <div key={product.id} className="product-card" onClick={() => navigate(`/product/${product.id}`)}>
                      <div className="product-card-inner">
                        <div className="product-image-wrapper">
                        <div className="product-badges">
                          {!product.inStock && <span className="badge out-of-stock">{product.isSold ? 'SOLD' : 'OUT OF STOCK'}</span>}
                        </div>
                        
                        <button 
                          className={`favorite-btn ${isFavorite(product.id) ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(product.id);
                          }}
                          title={isFavorite(product.id) ? "Remove from favorites" : "Add to favorites"}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill={isFavorite(product.id) ? "#ffffff" : "none"} xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L15.09 8.26L22 9L17 13.74L18.18 20.74L12 17.77L5.82 20.74L7 13.74L2 9L8.91 8.26L12 2Z" stroke="#ffffff" strokeWidth="1.5"/>
                          </svg>
                        </button>

                        <div className="product-images">
                          <img 
                            src={getImageUrl(product.images[0])} 
                            alt={product.name}
                            className="primary-image"
                            onError={handleImageError}
                          />
                          {(product.hoverImage || product.images[1]) && (
                            <img 
                              src={getImageUrl(product.hoverImage || product.images[1])} 
                              alt={product.name}
                              className="hover-image"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                        </div>

                      </div>

                      <div className="product-info">
                        <Link to={`/product/${product.id}`} className="product-name-link">
                          <h3 className="product-name">{product.name}</h3>
                        </Link>

                        <div className="product-footer">
                          <div className="price-section">
                            <span className="current-price">€{product.price}</span>
                            {product.originalPrice && product.originalPrice > product.price && (
                              <span className="original-price">€{product.originalPrice}</span>
                            )}
                          </div>

                          <button 
                            className={`add-to-cart-btn ${Array.isArray(cartState.items) && cartState.items.some(item => item.productId === product.id) ? 'added' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToCart(product.id, product.size);
                            }}
                            disabled={!product.inStock}
                          >
                            {product.isSold ? 'Sold' : !product.inStock ? 'Out of Stock' : 
                             Array.isArray(cartState.items) && cartState.items.some(item => item.productId === product.id) ? (
                               <>
                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '6px'}}>
                                   <polyline points="20,6 9,17 4,12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                 </svg>
                                 In Cart
                               </>
                             ) : 'Add to Cart'}
                          </button>
                        </div>
                      </div>
                      </div>
                    </div>
                  ))
                ) : (
                  /* Empty State */
                  <div className="empty-state">
                    <div className="empty-icon">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="11" cy="11" r="8" stroke="#900e9b" strokeWidth="2"/>
                        <path d="M21 21L16.65 16.65" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3>No products found</h3>
                    <p>Try adjusting your filters or search terms</p>
                    <button className="clear-filters-btn" onClick={clearFilters}>
                      Clear All Filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Size Selection Modal */}
      {showSizeModal && (
        <div className="modal-overlay" onClick={() => setShowSizeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select Category First</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowSizeModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Please select a category first to see available sizes for that category.</p>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn secondary" 
                onClick={() => setShowSizeModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;